import { useContext, useRef, useState, useEffect } from 'react';
import { InputGroup, FormControl, Button, Spinner } from 'react-bootstrap';

import { ContentContext } from '../Context/ContentContext';
import { useQuery } from 'react-query';
import { CouponContext } from '../Context/CouponContext';
import { TrashIcon } from '../../../../components/icons/TrashIcon';
import { toast } from '../../../../utils/toast';
import { couponsApi } from '../../../../api/coupons';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { apiFactory } from '../../../../api/ApiFactory';
import { ICoupon } from '../../../../api/coupons/interfaces/couponInterface';
import { trackBehavior } from '../PageBlocks/behavior-tracker';

const { getByCodeAndContent } = couponsApi;

export const Coupon = () => {
	const [code, setCode] = useState<string>('');

	const { content, quantity } = useContext(ContentContext);
	const { setCoupon, coupon } = useContext(CouponContext);

	const couponRef = useRef<HTMLInputElement>(null);

	const [isCouponInputVisible, setIsCouponInputVisible] = useState(false);

	const findCouponApi = useQuery(
		[getByCodeAndContent.key, { contentId: content?.id, code }],
		getByCodeAndContent.fetch,
		{
			enabled: false
		}
	);

	// Cupom pela URL (?cupom=X ou ?coupon=X) — link de campanha/recuperação
	// chega com o desconto pronto para aplicar.
	useEffect(() => {
		const queryParams = new URLSearchParams(window.location.search);
		const fromUrl = queryParams.get('cupom') || queryParams.get('coupon');
		if (fromUrl) setCode(fromUrl);
	}, []);

	// Exit intent (e outros widgets) aplicam cupom disparando este evento.
	useEffect(() => {
		const onApply = (event: Event) => {
			const detail = (event as CustomEvent<string>).detail;
			if (detail) setCode(String(detail));
		};
		window.addEventListener('waid:apply-coupon', onApply);
		return () =>
			window.removeEventListener('waid:apply-coupon', onApply);
	}, []);

	useEffect(() => {
		// Espera o produto resolver: disparar antes do content.id derrubava
		// cupons de URL com um "Cupom inválido" falso.
		if (!code || !content?.id) return;

		apiFactory
			.getInstance()
			.getCurseducaPayClient()
			.then((client) => {
				client
					.get<ICoupon>(`/coupons/${encodeURI(code)}/${content.id}`)
					.then(function ({ data }) {
						if (!data || !data?.isActive) {
							trackBehavior('coupon_failed', undefined, code);
							return toast.error('Cupom inválido.');
						}

						setCoupon(data);
						trackBehavior('coupon_applied', undefined, code);
						toast.success('Cupom aplicado com sucesso!');
					})
					.catch(function () {
						setCode('');
						trackBehavior('coupon_failed', undefined, code);
						return toast.error('Cupom inválido.');
					});
			});
	}, [code, content?.id]);

	const handleCouponApply = (value: string | undefined) => {
		if (!value || !content?.id) return;
		setCode(value);
	};

	const handleRemoveCoupon = () => {
		setCode('');
		setIsCouponInputVisible(false);
		setCoupon(null);
	};

	// Cupom aplicado (digitado ou via URL): chip com o código e o desconto no
	// lugar do link/input — o comprador VÊ o que ganhou e pode remover.
	if (coupon) {
		return (
			<div className="d-flex flex-column align-items-center m-0">
				<div
					className="d-flex align-items-center mt-4"
					style={{
						gap: 8,
						background: 'rgba(22,163,74,0.08)',
						border: '1px solid rgba(22,163,74,0.35)',
						color: '#15803d',
						borderRadius: 999,
						padding: '6px 14px',
						fontSize: 13,
						fontWeight: 600
					}}
				>
					<span>
						✓ Cupom {coupon.code}
						{coupon.flatDiscount
							? ` — R$ ${coupon.flatDiscount.toFixed(2)} off`
							: ''}
					</span>
					<button
						type="button"
						onClick={handleRemoveCoupon}
						aria-label="Remover cupom"
						style={{
							background: 'none',
							border: 0,
							color: 'inherit',
							cursor: 'pointer',
							lineHeight: 1,
							padding: 0
						}}
					>
						×
					</button>
				</div>
				{quantity + coupon.used > coupon.amount && (
					<span className="text-danger">
						Apenas {coupon.amount - coupon.used}{' '}
						{coupon.amount - coupon.used - quantity >= 0
							? 'cupons disponíveis e serão aplicados'
							: 'cupom disponível e será aplicado'}
					</span>
				)}
			</div>
		);
	}

	return (
		<div className="d-flex flex-column align-items-center m-0">
			{isCouponInputVisible ? (
				<InputGroup className="mb-2 mt-4">
					<FormControl
						className="border-gray-500 text-gray-800"
						style={{ background: 'none' }}
						placeholder="Insira o cupom"
						ref={couponRef}
					/>
					<InputGroup.Append>
						{coupon ? (
							<Button
								variant="danger"
								size="sm"
								onClick={handleRemoveCoupon}
							>
								<span className="svg-icon svg-icon-lg">
									<TrashIcon />
								</span>
							</Button>
						) : (
							<Button
								style={{ width: '70px' }}
								disabled={
									findCouponApi.isLoading ? true : false
								}
								variant="primary"
								size="sm"
								onClick={() =>
									handleCouponApply(couponRef.current?.value)
								}
							>
								{findCouponApi.isLoading ? (
									<Spinner animation="border" size="sm" />
								) : (
									'Aplicar'
								)}
							</Button>
						)}
					</InputGroup.Append>
				</InputGroup>
			) : (
				<a
					href="#"
					className="font-size-md text-gray-700 border border-gray-600 b p-3 rounded"
					onClick={() => setIsCouponInputVisible(true)}
				>
					Adicionar cupom de desconto
				</a>
			)}
		</div>
	);
};
